import { supabase } from '../lib/supabase';
import { PaykuService } from './paykuService';

export class TransactionVerifier {
  /**
   * Verifica si una transacción ha sido cancelada en algún momento.
   * Consulta varias fuentes para asegurar que no se acrediten monedas por pagos cancelados.
   */
  public static async verifyTransactionWasCancelled(transactionId: string): Promise<{
    cancelled: boolean;
    source?: string;
    details?: any;
  }> {
    console.log(`[VERIFY] Verificando si la transacción ${transactionId} fue cancelada...`);
    
    try {
      // 1. Verificar en nuestra base de datos si ya está marcada como cancelada
      const { data: localTransaction, error: localError } = await supabase
        .from('transactions')
        .select('*')
        .eq('transaction_id', transactionId)
        .eq('status', 'cancelled')
        .maybeSingle();
        
      if (localTransaction) {
        console.log(`[VERIFY] Transacción ${transactionId} ya está marcada como cancelada en nuestra base de datos`);
        return { 
          cancelled: true,
          source: 'local_database',
          details: localTransaction
        };
      }
      
      // 2. Verificar con el servicio de Payku
      const paykuService = PaykuService.getInstance();
      const wasCancelled = await paykuService.checkPaymentWasCancelled(transactionId);
      
      if (wasCancelled) {
        console.log(`[VERIFY] Payku confirma que la transacción ${transactionId} fue cancelada`);
        return {
          cancelled: true,
          source: 'payku_api',
          details: { checked_at: new Date().toISOString() }
        };
      }
      
      // 3. Verificar en localStorage y sessionStorage que podrían indicar una cancelación
      const hasLocalCancellationMarker = this.checkLocalStorageForCancellation(transactionId);
      if (hasLocalCancellationMarker) {
        console.log(`[VERIFY] Se encontraron indicadores de cancelación en localStorage para ${transactionId}`);
        return {
          cancelled: true,
          source: 'local_storage',
          details: hasLocalCancellationMarker
        };
      }
      
      // Si llegamos aquí, no hay evidencia de que la transacción haya sido cancelada
      console.log(`[VERIFY] No hay evidencia de que la transacción ${transactionId} haya sido cancelada`);
      return { cancelled: false };
      
    } catch (error) {
      console.error(`[VERIFY] Error al verificar si la transacción ${transactionId} fue cancelada:`, error);
      // En caso de error, es mejor prevenir que lamentar: considerar como no cancelada
      return { 
        cancelled: false,
        details: { 
          error: error instanceof Error ? error.message : 'Error desconocido',
          error_at: new Date().toISOString()
        }
      };
    }
  }
  
  /**
   * Busca en localStorage y sessionStorage indicadores de que una transacción haya sido cancelada
   */
  private static checkLocalStorageForCancellation(transactionId: string): any | null {
    try {
      // Verificar marcadores específicos que podrían indicar cancelación
      const cancelledTxsStr = localStorage.getItem('cancelled_transactions');
      if (cancelledTxsStr) {
        try {
          const cancelledTxs = JSON.parse(cancelledTxsStr);
          if (Array.isArray(cancelledTxs) && cancelledTxs.includes(transactionId)) {
            return { source: 'cancelled_transactions_array' };
          }
        } catch {}
      }
      
      // Verificar si hay un objeto específico para esta transacción
      const txCancelInfoStr = localStorage.getItem(`tx_cancel_${transactionId}`);
      if (txCancelInfoStr) {
        try {
          return { source: 'specific_tx_cancel_record', data: JSON.parse(txCancelInfoStr) };
        } catch {}
      }
      
      return null;
    } catch (error) {
      console.error('[VERIFY] Error al verificar localStorage:', error);
      return null;
    }
  }
  
  /**
   * Marcar una transacción como cancelada en nuestra base de datos
   */
  public static async markTransactionAsCancelled(transactionId: string, reason: string): Promise<boolean> {
    try {
      console.log(`[VERIFY] Marcando transacción ${transactionId} como cancelada. Razón: ${reason}`);
      
      // Buscar la transacción
      const { data: transaction, error: findError } = await supabase
        .from('transactions')
        .select('*')
        .eq('transaction_id', transactionId)
        .maybeSingle();
      
      if (findError && findError.code !== 'PGRST116') { // PGRST116 = no se encontró
        console.error(`[VERIFY] Error al buscar transacción ${transactionId}:`, findError);
        return false;
      }
      
      if (transaction) {
        // Si ya existe, actualizar su estado
        const { error } = await supabase
          .from('transactions')
          .update({ 
            status: 'cancelled',
            updated_at: new Date().toISOString(),
            metadata: {
              ...transaction.metadata,
              cancelled_at: new Date().toISOString(),
              cancel_reason: reason,
              original_status: transaction.status
            }
          })
          .eq('transaction_id', transactionId);
        
        if (error) {
          console.error(`[VERIFY] Error al actualizar transacción ${transactionId}:`, error);
          return false;
        }
      } else {
        // Si no existe la transacción, registrarla como "phantom transaction"
        // Esto es extraño pero puede ocurrir si el flujo de pago no completó correctamente
        console.log(`[VERIFY] Transacción ${transactionId} no encontrada, registrando como 'phantom transaction'`);
        
        // Recuperar el ID de usuario de la sesión actual
        const { data: { session } } = await supabase.auth.getSession();
        const userId = session?.user?.id;
        
        if (!userId) {
          console.error(`[VERIFY] No se pudo obtener ID de usuario para registrar transacción fantasma`);
          return false;
        }
        
        const { error: insertError } = await supabase
          .from('transactions')
          .insert({
            transaction_id: transactionId,
            user_id: userId,
            status: 'cancelled',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            metadata: {
              phantom: true,
              cancel_reason: reason,
              cancelled_at: new Date().toISOString(),
              detected_at: new Date().toISOString()
            }
          });
        
        if (insertError) {
          console.error(`[VERIFY] Error al crear registro de transacción fantasma:`, insertError);
          return false;
        }
      }
      
      // Registrar en localStorage para referencia futura
      try {
        // Añadir a la lista de transacciones canceladas
        const cancelledTxsStr = localStorage.getItem('cancelled_transactions');
        let cancelledTxs = [];
        
        if (cancelledTxsStr) {
          try {
            cancelledTxs = JSON.parse(cancelledTxsStr);
            if (!Array.isArray(cancelledTxs)) cancelledTxs = [];
          } catch {}
        }
        
        if (!cancelledTxs.includes(transactionId)) {
          cancelledTxs.push(transactionId);
          localStorage.setItem('cancelled_transactions', JSON.stringify(cancelledTxs));
        }
        
        // Guardar detalles específicos de esta cancelación
        localStorage.setItem(`tx_cancel_${transactionId}`, JSON.stringify({
          cancelled_at: new Date().toISOString(),
          reason: reason
        }));
      } catch (storageError) {
        console.error(`[VERIFY] Error al guardar en localStorage:`, storageError);
        // No fallamos la operación principal si falla el localStorage
      }
      
      return true;
    } catch (error) {
      console.error(`[VERIFY] Error general al marcar transacción como cancelada:`, error);
      return false;
    }
  }
  
  /**
   * Verificar y revertir transacciones que fueron incorrectamente acreditadas
   */
  public static async validateAndRevertIncorrectCredits(userId: string): Promise<{
    corrected: boolean;
    count: number;
    details: any[];
  }> {
    try {
      console.log(`[VERIFY] Validando y corrigiendo transacciones para usuario ${userId}`);
      
      // Establecer fecha límite (últimos 7 días)
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      const isoDate = sevenDaysAgo.toISOString();
      
      console.log(`[VERIFY] Buscando transacciones desde: ${isoDate}`);
      
      // Obtener transacciones exitosas para el usuario en los últimos 7 días
      const { data: successTransactions, error } = await supabase
        .from('transactions')
        .select('*')
        .eq('user_id', userId)
        .eq('status', 'success')
        .gte('created_at', isoDate)
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('[VERIFY] Error al obtener transacciones:', error);
        return { corrected: false, count: 0, details: [] };
      }
      
      if (!successTransactions || successTransactions.length === 0) {
        console.log('[VERIFY] No hay transacciones a validar');
        return { corrected: false, count: 0, details: [] };
      }
      
      console.log(`[VERIFY] Verificando ${successTransactions.length} transacciones recientes`);
      
      // Verificar cada transacción
      const paykuService = PaykuService.getInstance();
      const incorrectTransactions = [];
      
      for (const tx of successTransactions) {
        try {
          console.log(`[VERIFY] Verificando transacción ${tx.transaction_id} (${new Date(tx.created_at).toLocaleString()})`);
          const wasCancelled = await paykuService.checkPaymentWasCancelled(tx.transaction_id);
          
          if (wasCancelled) {
            console.log(`[VERIFY] Transacción ${tx.transaction_id} fue incorrectamente acreditada`);
            incorrectTransactions.push(tx);
          }
        } catch (txError) {
          console.error(`[VERIFY] Error al verificar transacción ${tx.transaction_id}:`, txError);
          // Continuar con la siguiente transacción
        }
      }
      
      if (incorrectTransactions.length === 0) {
        console.log('[VERIFY] No se encontraron transacciones incorrectas');
        return { corrected: false, count: 0, details: [] };
      }
      
      console.log(`[VERIFY] Se encontraron ${incorrectTransactions.length} transacciones incorrectas`);
      
      // Corregir cada transacción
      const correctionDetails = [];
      
      for (const tx of incorrectTransactions) {
        try {
          // 1. Cambiar estado a cancelled
          const { error: updateError } = await supabase
            .from('transactions')
            .update({
              status: 'cancelled',
              updated_at: new Date().toISOString(),
              metadata: {
                ...tx.metadata,
                correction: {
                  original_status: tx.status,
                  corrected_at: new Date().toISOString(),
                  reason: 'automatic_validation'
                }
              }
            })
            .eq('transaction_id', tx.transaction_id);
          
          if (updateError) {
            console.error(`[VERIFY] Error al actualizar transacción ${tx.transaction_id}:`, updateError);
            continue;
          }
          
          // 2. Restar monedas acreditadas incorrectamente
          if (tx.vcoins_amount && tx.vcoins_amount > 0) {
            const { data: userCoins, error: coinsError } = await supabase
              .from('user_coins')
              .select('*')
              .eq('user_id', userId)
              .single();
              
            if (coinsError) {
              console.error(`[VERIFY] Error al obtener saldo de usuario:`, coinsError);
              continue;
            }
            
            if (!userCoins) {
              console.error(`[VERIFY] No se encontró saldo para el usuario ${userId}`);
              continue;
            }
            
            // Calcular nuevo saldo
            let currentBalance = 0;
            if ('balance' in userCoins && userCoins.balance !== null) {
              currentBalance = userCoins.balance;
            } else if ('coins' in userCoins && userCoins.coins !== null) {
              currentBalance = userCoins.coins;
            }
            
            const newBalance = Math.max(0, currentBalance - tx.vcoins_amount);
            
            // Actualizar saldo
            const { error: updateCoinsError } = await supabase
              .from('user_coins')
              .update({
                balance: newBalance,
                coins: newBalance,
                updated_at: new Date().toISOString()
              })
              .eq('user_id', userId);
              
            if (updateCoinsError) {
              console.error(`[VERIFY] Error al actualizar saldo:`, updateCoinsError);
              continue;
            }
            
            console.log(`[VERIFY] Saldo corregido: ${currentBalance} -> ${newBalance} (-${tx.vcoins_amount})`);
            
            // 3. Registrar corrección en historial
            const { error: historyError } = await supabase
              .from('coin_history')
              .insert({
                user_id: userId,
                amount: -tx.vcoins_amount,
                type: 'correction',
                description: `Corrección automática de transacción cancelada ${tx.transaction_id}`,
                created_at: new Date().toISOString(),
                metadata: {
                  transaction_id: tx.transaction_id,
                  correction_type: 'invalid_transaction',
                  previous_balance: currentBalance,
                  new_balance: newBalance
                }
              });
              
            if (historyError) {
              console.error(`[VERIFY] Error al registrar historial:`, historyError);
              // No fallamos la operación principal si falla el historial
            }
            
            // Añadir detalles de corrección
            correctionDetails.push({
              transaction_id: tx.transaction_id,
              amount: tx.vcoins_amount,
              previous_balance: currentBalance,
              new_balance: newBalance
            });
          }
        } catch (correctionError) {
          console.error(`[VERIFY] Error al corregir transacción ${tx.transaction_id}:`, correctionError);
        }
      }
      
      return { 
        corrected: correctionDetails.length > 0, 
        count: correctionDetails.length,
        details: correctionDetails
      };
    } catch (error) {
      console.error('[VERIFY] Error general en validateAndRevertIncorrectCredits:', error);
      return { corrected: false, count: 0, details: [] };
    }
  }
} 